import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Settings, LogOut, User, FolderKanban, MessageSquare, X } from "lucide-react";

import { PIPELINE_API } from "./project-tabs/constants";

const API = PIPELINE_API;

export const UserProfilePopup = ({ onClose }) => {
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const popupRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/profile`, { withCredentials: true });
        setProfile(data && typeof data === 'object' ? data : null);
      } catch (e) {
        console.error("Failed to load profile:", e);
      }
    })();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target)) onClose();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const initials = user?.name?.split(" ")?.map(w => w[0]).join("").toUpperCase().slice(0, 2) || "U";

  return (
    <div
      ref={popupRef}
      className="absolute bottom-16 left-2 w-64 bg-white rounded-2xl shadow-elevated border border-border/60 overflow-hidden z-50"
      style={{ animation: "cardFadeIn 0.2s ease-out" }}
      data-testid="user-profile-popup"
    >
      {/* Header */}
      <div className="relative px-5 pt-5 pb-4">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-6 h-6 rounded-lg text-foreground/50 hover:text-foreground hover:bg-foreground/5 flex items-center justify-center"
          data-testid="close-profile-popup"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#6baed6] to-[#178a55] flex items-center justify-center text-white text-sm font-bold shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{user?.name || "User"}</p>
            <p className="text-[11px] text-foreground/40 truncate">{user?.email}</p>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-[#014d7d]/8 text-[#014d7d] capitalize mt-1 inline-block">
              {user?.role?.replace(/_/g, " ")}
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      {profile && (
        <div className="flex border-t border-b border-border/40 divide-x divide-border/40">
          <div className="flex-1 text-center py-3">
            <p className="text-lg font-heading font-bold text-foreground">{profile.project_count}</p>
            <p className="text-[10px] text-foreground/40 font-semibold">Projects</p>
          </div>
          <div className="flex-1 text-center py-3">
            <p className="text-lg font-heading font-bold text-foreground">{profile.session_count}</p>
            <p className="text-[10px] text-foreground/40 font-semibold">Sessions</p>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="py-2">
        <Link
          to="/settings"
          onClick={onClose}
          className="flex items-center gap-3 px-5 py-2.5 text-sm text-foreground/60 hover:text-foreground hover:bg-foreground/[0.03]"
          data-testid="profile-settings-link"
        >
          <Settings className="w-4 h-4" strokeWidth={1.7} />
          Settings
        </Link>
        <button
          onClick={() => { logout(); onClose(); }}
          className="w-full flex items-center gap-3 px-5 py-2.5 text-sm text-red-500/70 hover:text-red-600 hover:bg-red-50/50"
          data-testid="profile-logout-btn"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.7} />
          Log out
        </button>
      </div>
    </div>
  );
};
